/**
 * ============================================================================
 * VINPOP — Product Rating System
 * ============================================================================
 *
 * Star rating component for wine products. Allows logged-in customers to
 * rate wines and optionally submit food pairing suggestions.
 * Saves ratings to the VinPOP API and displays aggregate scores.
 *
 * Loaded from: layout/theme.liquid (global, deferred)
 * CSS: product-rating.css
 * ============================================================================
 */

class ProductRating {
  constructor() {
    this.customerId = this.getCustomerId();
    this.init();
    this.initPairings();
  }

  getMetaContent(name) {
    const meta = document.querySelector(`meta[name="${name}"]`);
    return meta ? String(meta.getAttribute('content') || '').trim() : '';
  }

  buildVinpopAuthHeaders() {
    const timestamp = this.getMetaContent('friends-auth-timestamp');
    const signature = this.getMetaContent('friends-auth-signature');
    if (!this.customerId || !timestamp || !signature) {
      throw new Error('vinpop_auth_unavailable');
    }

    return {
      'Content-Type': 'application/json',
      'X-Customer-Id': String(this.customerId),
      'X-Timestamp': timestamp,
      'X-Signature': signature
    };
  }

  getCustomerId() {
    const metaTag = document.querySelector('meta[name="customer-id"]');
    if (metaTag) {
      return metaTag.getAttribute('content');
    }
    return null;
  }

  /**
   * Logged-out visitors get the shared "log in to rate this wine" modal
   * instead of a silent no-op. Returns true when the action may proceed.
   */
  async requireLogin(context) {
    if (this.customerId) return true;

    if (window.VinPopLoginPrompt) {
      const allowed = await window.VinPopLoginPrompt.requireLogin({ context: context });
      if (allowed) {
        // Session was valid after all (stale cached HTML) - pick the id back up.
        this.customerId = this.getCustomerId();
      }
      return allowed;
    }

    // Fallback if the shared module failed to load.
    const returnUrl = window.location.pathname + window.location.search;
    window.location.href = '/account/login?return_to=' + encodeURIComponent(returnUrl);
    return false;
  }

  init() {
    const ratingContainers = document.querySelectorAll('.product-rating');

    ratingContainers.forEach(container => {
      const buttons = container.querySelectorAll('.product-rating__btn');
      const feedback = container.querySelector('.product-rating__feedback');

      buttons.forEach(button => {
        button.addEventListener('click', async (e) => {
          if (!(await this.requireLogin())) return;
          const rating = button.getAttribute('data-rating');
          await this.handleRating(container, rating, buttons, feedback);
        });
      });
    });
  }

  initPairings() {
    const pairingContainers = document.querySelectorAll('.product-pairing');

    pairingContainers.forEach(container => {
      const checkboxes = container.querySelectorAll('.product-pairing__checkbox');

      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', async (e) => {
          const pairing = checkbox.getAttribute('data-pairing');
          const productId = checkbox.getAttribute('data-product-id');
          const isChecked = checkbox.checked;

          if (!(await this.requireLogin('pairing'))) {
            // Revert the tick: nothing was saved.
            checkbox.checked = !isChecked;
            const revertedOption = checkbox.closest('.product-pairing__option');
            if (revertedOption) {
              revertedOption.classList.toggle('selected', checkbox.checked);
            }
            return;
          }

          // Add/remove selected class for visual feedback
          const option = checkbox.closest('.product-pairing__option');
          if (option) {
            if (isChecked) {
              option.classList.add('selected');
            } else {
              option.classList.remove('selected');
            }
          }

          await this.handlePairing(productId, pairing, isChecked);
        });
      });
    });
  }

  async handleRating(container, rating, buttons, feedback) {
    if (!this.customerId) {
      console.log('No customer logged in [FIXED]');
      return;
    }

    const clickedButton = container.querySelector(`[data-rating="${rating}"]`);
    const wasSelected = clickedButton.classList.contains('selected');

    // Remove selected class from all buttons
    buttons.forEach(btn => btn.classList.remove('selected'));

    const productId = clickedButton.getAttribute('data-product-id') || container.getAttribute('data-product-id') || this.getProductId();
    if (!productId) {
      console.error('No product ID found');
      return;
    }

    if (!wasSelected) {
      // Add rating - either new or change existing
      clickedButton.classList.add('selected');

      // Show feedback
      if (feedback) {
        feedback.style.display = 'block';
        const thankYou = feedback.querySelector('.product-rating__thank-you');
        if (thankYou) {
          thankYou.textContent = 'Thank you for your feedback!';
        }
      }

      // Convert rating to number format (love=3, like=2, dislike=1)
      const ratingNumber = this.convertRatingToNumber(rating);

      // Save rating to PostgreSQL
      await this.saveRating(productId, ratingNumber);

      // Trigger sync to Shopify metafields
      window.dispatchEvent(new CustomEvent('ratingUpdated', {
        detail: { productId, rating: ratingNumber }
      }));

      // Optional: Send rating to analytics
      this.trackRating(rating);

      // Pourquoi ce dislike : fenetre de vinpop-tracking.js, reponse envoyee au dashboard.
      if (rating === 'dislike' && window.VinPopTracking && typeof window.VinPopTracking.openDislikeReasons === 'function') {
        const titleEl = document.querySelector('h1');
        window.VinPopTracking.openDislikeReasons({
          productId,
          productTitle: titleEl ? titleEl.textContent.trim() : '',
          source: 'product_page'
        });
      }

    } else {
      // Second click on same button - remove rating from database
      if (feedback) {
        feedback.style.display = 'none';
      }

      // Delete rating from PostgreSQL
      await this.deleteRating(productId);

      // Trigger sync to Shopify metafields
      window.dispatchEvent(new CustomEvent('ratingUpdated', {
        detail: { productId, rating: null }
      }));
    }
  }

  convertRatingToNumber(rating) {
    // Convert string rating to number format for PostgreSQL
    // Unified mapping: love=3, like=2, dislike=1
    // IMPORTANT: Return actual numbers, not strings!
    switch(rating) {
      case 'love': return 3;
      case 'like': return 2;
      case 'dislike': return 1;
      default: return 2;
    }
  }

  convertNumberToRating(number) {
    // Convert number format back to string rating
    // Unified mapping: 3=love, 2=like, 1=dislike
    // Handle both string and number inputs
    const num = String(number);
    switch(num) {
      case '3': return 'love';
      case '2': return 'like';
      case '1': return 'dislike';
      default: return null;
    }
  }

  getProductId() {
    // Try to get product ID from the page
    const productIdElement = document.querySelector('[data-product-id]');
    if (productIdElement) {
      return productIdElement.getAttribute('data-product-id');
    }

    // Fallback: try to extract from URL
    const pathSegments = window.location.pathname.split('/');
    const productIndex = pathSegments.indexOf('products');

    if (productIndex !== -1 && pathSegments[productIndex + 1]) {
      return pathSegments[productIndex + 1];
    }

    return null;
  }

  async saveRating(productId, rating) {
    const payload = {
      id: productId,
      customerId: this.customerId,
      rating: rating
    };

    try {
      const response = await fetch('https://api.vinpop.fr/api/ratings/save', {
        method: 'POST',
        headers: this.buildVinpopAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server error response:', errorText);
      }
    } catch (error) {
      console.error('❌ Error saving rating:', error);
    }
  }

  async deleteRating(productId) {
    try {
      const response = await fetch(`https://api.vinpop.fr/api/ratings/delete?customerId=${this.customerId}&id=${productId}`, {
        method: 'DELETE',
        headers: this.buildVinpopAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server error response:', errorText);
      }
    } catch (error) {
      console.error('❌ Error deleting rating:', error);
    }
  }


  trackRating(rating) {
    // Optional: Send to analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'product_rating', {
        'rating': rating,
        'product_id': this.getProductId()
      });
    }

  }

  async handlePairing(productId, food, value) {
    if (!this.customerId) {
      console.log('No customer logged in');
      return;
    }

    const payload = {
      id: productId,
      customerId: this.customerId,
      food: food,
      value: value
    };

    try {
      const response = await fetch('https://api.vinpop.fr/api/pairings/save', {
        method: 'POST',
        headers: this.buildVinpopAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server error response:', errorText);

        // Show user-friendly message for unsupported options
        if (response.status === 400 || response.status === 500) {
          // Find the checkbox that was clicked
          const containers = document.querySelectorAll('.product-pairing');
          containers.forEach(container => {
            const checkbox = container.querySelector(`[data-pairing="${food}"]`);
            if (checkbox) {
              // Uncheck the option that failed
              checkbox.checked = false;
              const option = checkbox.closest('.product-pairing__option');
              if (option) {
                option.classList.remove('selected');
              }

              // Show temporary message
              const label = checkbox.closest('.product-pairing__option').querySelector('.product-pairing__label');
              const originalText = label.textContent;
              label.textContent = 'Coming Soon';
              label.style.opacity = '0.5';

              setTimeout(() => {
                label.textContent = originalText;
                label.style.opacity = '1';
              }, 2000);
            }
          });
        }
      }
    } catch (error) {
      console.error('❌ Error saving pairing:', error);
    }
  }

  async loadSavedPairings() {
    if (!this.customerId) {
      return;
    }

    const productId = this.getProductId();
    if (!productId) {
      return;
    }

    try {
      // Get pairings from PostgreSQL
      const response = await fetch(`https://api.vinpop.fr/api/pairings/get?customerId=${this.customerId}&id=${productId}`, {
        headers: this.buildVinpopAuthHeaders()
      });
      const pairings = await response.json();

      if (pairings && pairings.length > 0) {
        const pairingData = pairings[0];
        const container = document.querySelector('.product-pairing');

        if (container) {
          // Check each food pairing checkbox based on saved data
          Object.keys(pairingData).forEach(food => {
            if (pairingData[food] === true) {
              const checkbox = container.querySelector(`[data-pairing="${food}"]`);
              if (checkbox) {
                checkbox.checked = true;
                const option = checkbox.closest('.product-pairing__option');
                if (option) {
                  option.classList.add('selected');
                }
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('❌ Error loading saved pairings:', error);
    }
  }

  async loadSavedRating() {
    if (!this.customerId) {
      return;
    }

    const productId = this.getProductId();
    if (!productId) {
      return;
    }

    try {
      // Get ratings from PostgreSQL
      const response = await fetch(`https://api.vinpop.fr/api/ratings/getAll?customerId=${this.customerId}`, {
        headers: this.buildVinpopAuthHeaders()
      });
      const ratingsArray = await response.json();

      if (ratingsArray && ratingsArray.length > 0) {
        const ratingByProductId = new Map(
          ratingsArray.map(item => [String(item.id), item])
        );

        document.querySelectorAll('.product-rating').forEach(container => {
          const containerProductId = container.getAttribute('data-product-id') || this.getProductId();
          const productRating = ratingByProductId.get(String(containerProductId));

          if (productRating) {
            const ratingString = this.convertNumberToRating(productRating.rating);
            const button = container.querySelector(`[data-rating="${ratingString}"]`);
            const feedback = container.querySelector('.product-rating__feedback');

            if (button) {
              button.classList.add('selected');
            }

            if (feedback) {
              feedback.style.display = 'block';
            }
          }
        });
      }
    } catch (error) {
      console.error('❌ Error loading saved rating:', error);
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const productRating = new ProductRating();

  // Load any previously saved rating and pairings
  setTimeout(() => {
    productRating.loadSavedRating();
    productRating.loadSavedPairings();
  }, 500);
});
